import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@mui/material/styles/styled";
import {
  getGenericRelationText,
  getQFAndRefinementsLines,
} from "fe-common/logic";
import { Suggestion } from "../hooks/useSuggestions";
import { YesNoButtonComponent } from "../common/YesNoButtonComponent";
import { LevelSelectComponent } from "../common/LevelSelectComponent";
import { findingUsesLevel } from "../useDataInputPanels";
import { DataInput, mergeResponseToQF } from "../../dataInputTypes";
import { Button, Theme } from "@mui/material";
import { NextStepByDiseaseInfo } from "./NextStepByDiseaseInfo";
import { useFindingClinicalReason } from "../refineFeature/useFindingClinicalReason";
import { ClinicalReasoningTooltip } from "../common/ClinicalReasoningTooltip";
import { RefineIcon } from "../common/refineIcon";
import { DesktopOnly } from "../util/desktopOnly";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { MobileOnly } from "../util/mobileOnly";
import { DiseaseCategoryType, QualifiedFeature } from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { useScreenSize } from "../util/useScreenSize";
import { RefinePopup } from "../refineFeature/refinePopup";
import { clone } from "lodash";

import {
  PatientCaseGrouping,
  RefinePopupContextProps,
} from "../../../context/diagnosticAgent";
import { PatientCaseChange } from "../usePatientCase";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ClinicalReasoningIconMobile } from "../common/clinicalReasoningIconMobile";
import { PanelsUtil } from "../util/PanelsUtil";
import { RefineFeatureInline } from "./refineFeatureInline";
//region [[ Styles ]]

const NextStepPanelView = styled("div")(({ theme }) => ({
  display: "flex",
  backgroundColor: RosebudColorMap.yellow,
  flexDirection: "column",
  minHeight: "50px",
  padding: "15px",
  width: "100%",
}));

const FindingTitle = styled("div")(({ theme }) => ({
  marginRight: "7px",
  marginBottom: "4px",
  width: "auto",
  minWidth: "unset",
  fontSize: "16px",
  color: "black",
  display: "inline-flex",
  alignItems: "center",
  [theme.breakpoints.down(MobileView)]: {
    cursor: "pointer",
  },
}));

const FindingWithQualifier = styled("div")({
  display: "flex",
  flexFlow: "column",
  marginRight: "5px",
});

const Question = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
  flexWrap: "wrap",
}));

const ValueArea = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  justifyContent: "flex-end",
  alignItems: "center",
}));

const ShowMoreContainer = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: "5px",
  justifyContent: "flex-end",
}));

const TitleContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  alignItems: "center",
}));

const LevelSelect = styled(LevelSelectComponent)(({ theme }) => ({
  width: "100%",
  flex: 1,
}));

const ShowMore = styled("div")(({ theme }) => ({
  display: "flex",
  cursor: "pointer",
}));

const AutoWidthDesktopOnly = styled(DesktopOnly)({
  width: "auto",
});

const ClinicalReasoningIcon = styled("div")({
  paddingTop: "6px",
});

const MobileClinicalReasoning = styled(ClinicalReasoningTooltip)(
  ({ theme }) => ({
    marginBottom: "0px",
    padding: "8px",
    margin: "10px",
    marginLeft: "5px",

    [theme.breakpoints.down(MobileView)]: {
      padding: "0px",
      marginTop: "10px",
      margin: "0px",
    },
  }),
);

export const RefineButton = styled(Button)(
  ({ theme, selected }: { theme?: Theme; selected: "true" | "false" }) => ({
    color: selected === "true" ? "white" : KahunColorMap.PrimaryDim,
    backgroundColor:
      selected === "true" ? KahunColorMap.PrimaryDim : "transparent",
    border: "1px solid " + KahunColorMap.PrimaryDim,

    "&:focus, &:hover, &:active": {
      color: selected === "true" ? "white" : KahunColorMap.PrimaryDim,
      backgroundColor:
        selected === "true" ? KahunColorMap.PrimaryDim : "transparent",
      border: "1px solid " + KahunColorMap.PrimaryDim,
    },

    fontSize: "14px",
    minHeight: "33px",
    height: "max-content",
    letterSpacing: "0",
    textTransform: "capitalize",
    lineHeight: "14px",
    borderRadius: "6.25px",

    [theme.breakpoints.up(MobileView)]: {
      width: "157px",
    },
    [theme.breakpoints.down(MobileView)]: {
      width: "112px",
    },
  }),
);

export const HideClinicalReasoningIcon = styled(RemoveCircleOutlineIcon)({
  color: KahunColorMap.Accent,
});

export const RefineButtonFrame = styled("div")({});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface NextStepPanelProps {
  className?: string;
  suggestion?: Suggestion;
  loading?: boolean;
  skipSuggestion?: () => void;
  onAddFinding?: (resp: any) => void;
  onRemoveFinding?: (qf: QualifiedFeature) => void;
  color?: string;
  showActions?: boolean;
  hideSelectComponent?: boolean;
  showInlineReasoning?: boolean;
  response?: DataInput;
}

//endregion [[ Props ]]

//region [[ Functions ]]
/*const noQualifiersByDisease = (byDisease): boolean =>
  !byDisease.suggestion?.qualifiers?.length;*/
const isRefinementSuggestion = (s: Suggestion): boolean =>
  !!s?.finding?.qualifiers?.length &&
  s.category !== DiseaseCategoryType.OutcomeTest;
//endregion [[ Functions ]]

export const NextStepPanel = React.memo(
  ({
    suggestion,
    onRemoveFinding,
    onAddFinding,
    skipSuggestion,
    response,
    hideSelectComponent,
    ...props
  }: NextStepPanelProps) => {
    const [showClinicalReasoning, setShowClinicalReasoning] = useState(false);
    const [showRefine, setShowRefine] = useState(false);

    const relationId =
      suggestion.finding && suggestion.finding.qualifiers.length
        ? suggestion.finding.qualifiers.filter((q) => !q.value)[0].relation.id
        : null;

    const getQfWithQualifiers = useCallback(() => {
      let mergedQf = {
        ...suggestion.finding,
      };

      if (
        response &&
        response.qualifiers &&
        response.qualifiers.some((q) => q.relation.id === relationId)
      ) {
        mergedQf = mergeResponseToQF(mergedQf, {
          qualifiers: response.qualifiers.filter(
            (q) => q.relation.id === relationId,
          ),
        });
      }

      return mergedQf;
    }, [response, suggestion, relationId]);

    const [refineContext, setRefineContext] = useState<RefinePopupContextProps>(
      {
        open: false,
        refineQualifier: getQfWithQualifiers(),
        patientCaseGrouping: PatientCaseGrouping.CLINICAL_FEATURES,
      },
    );

    const { isMobile } = useScreenSize();

    const {
      findingTitle,
      relationText,
      findingQualifiersText,
    } = useMemo(() => {
      const qf = getQfWithQualifiers();

      let [findingTitle, findingQualifiersText] = getQFAndRefinementsLines(qf);

      const relationText = (isRefinementSuggestion(suggestion)
        ? getGenericRelationText(suggestion.finding)
        : ""
      ).toString();

      if (relationText && findingQualifiersText) {
        findingQualifiersText =
          relationText +
          ": " +
          findingQualifiersText
            .replace(new RegExp(relationText, "g"), "")
            .replace(" ,", ",");
      }

      findingQualifiersText = PanelsUtil.addEllipsis(findingQualifiersText, 45);

      return { findingTitle, relationText, findingQualifiersText };
    }, [suggestion, getQfWithQualifiers]);

    const diseasesStrength = useFindingClinicalReason(
      suggestion?.causedByDisease,
    )();

    useEffect(() => {
      setRefineContext((context) => ({
        ...context,
        causedByDisease: suggestion?.causedByDisease,
      }));
    }, [suggestion]);

    const isItLab = useMemo(() => {
      return suggestion?.finding && findingUsesLevel(suggestion.finding);
    }, [suggestion]);

    const trimFirstWord = useCallback((finding) => {
      let spaceIdx = finding.indexOf(" ");
      finding = finding.substring(spaceIdx);
      return finding;
    }, []);

    const onYesNoResponse = useCallback(
      (resp: DataInput) => {
        if (!resp) {
          if (onRemoveFinding) {
            onRemoveFinding(suggestion.finding);
          }
          return;
        } else if (resp.negate) {
          onAddFinding(resp);
        } else if (!isRefinementSuggestion(suggestion)) {
          onAddFinding(resp);
        } else {
          const respy = {
            ...resp,
            qualifiers: suggestion.finding.qualifiers,
          };
          onAddFinding(respy);
        }
      },
      [onAddFinding, suggestion, onRemoveFinding],
    );

    const onLevelSelectResponse = useCallback(
      (resp: DataInput) => {
        onAddFinding(resp);
      },
      [onAddFinding],
    );

    const onShowClinicalReasoning = () => {
      if (!isMobile) return;
      evt({
        category: CATEGORY.PLAN,
        action: showClinicalReasoning
          ? ACTION.HIDE_CLINICAL_REASONING
          : ACTION.SHOW_CLINICAL_REASONING,
        label: suggestion.finding.name,
      });

      setShowClinicalReasoning(!showClinicalReasoning);
    };

    const onRefineEditDone = () => {
      setShowRefine(false);
    };

    const onRefineDone = (e, change: PatientCaseChange) => {
      setRefineContext((context) => ({
        ...context,
        open: false,
      }));
      if (change) {
        let mergedQf = clone(suggestion.finding);
        mergedQf.qualifiers = [];
        if (response && response.qualifiers) {
          mergedQf = mergeResponseToQF(suggestion.finding, {
            qualifiers: response.qualifiers.filter(
              (q) => q.relation.id !== relationId,
            ),
          });
        }
        const existingQualifiers = mergedQf.qualifiers.filter((q) => q.value);
        const changesQualifiers = change.qf.qualifiers.filter((q) => q.value);
        onAddFinding({
          qualifiers: existingQualifiers.concat(changesQualifiers),
        });
      }
    };

    if (!suggestion?.finding || props.loading) return null;
    return (
      <NextStepPanelView className={props.className}>
        <RefinePopup
          showYesNo={false}
          showDelete={false}
          updatePatientCase={false}
          showDuration={false}
          context={refineContext}
          onDone={onRefineDone}
        />
        <Question>
          {!showRefine && (
            <TitleContainer>
              <AutoWidthDesktopOnly>
                <RefineIcon className={"finding-icon"} size={"medium"} />
              </AutoWidthDesktopOnly>
              <FindingTitle
                className={"finding-title"}
                onClick={
                  props.showInlineReasoning ? onShowClinicalReasoning : null
                }
              >
                <FindingWithQualifier>
                  {!isItLab ? findingTitle : trimFirstWord(findingTitle)}
                </FindingWithQualifier>
                {props.showInlineReasoning && (
                  <MobileOnly>
                    <ClinicalReasoningIconMobile
                      showClinicalReasoning={showClinicalReasoning}
                      onShowClinicalReasoning={onShowClinicalReasoning}
                    />
                  </MobileOnly>
                )}
              </FindingTitle>
              <ClinicalReasoningIcon>
                <AutoWidthDesktopOnly>
                  <NextStepByDiseaseInfo
                    title={
                      diseasesStrength &&
                      diseasesStrength.length &&
                      !isRefinementSuggestion(suggestion) && (
                        <ClinicalReasoningTooltip
                          suggestion={suggestion}
                          show={true}
                        />
                      )
                    }
                  />
                </AutoWidthDesktopOnly>
              </ClinicalReasoningIcon>
            </TitleContainer>
          )}
          {!hideSelectComponent && (
            <>
              {!showRefine && (
                <ValueArea>
                  {!isItLab ? (
                    isRefinementSuggestion(suggestion) ? (
                      <>
                        <RefineButtonFrame
                          onClick={() => {
                            /*setRefineContext((context) => ({
                              ...context,
                              open: true,
                            }))*/
                            setShowRefine(true);
                          }}
                        >
                          <RefineButton
                            selected={findingQualifiersText ? "true" : "false"}
                          >
                            {findingQualifiersText
                              ? findingQualifiersText
                              : relationText + "?"}
                          </RefineButton>
                        </RefineButtonFrame>
                      </>
                    ) : (
                      <YesNoButtonComponent
                        qf={suggestion.finding}
                        onChange={onYesNoResponse}
                        disabled={false}
                        response={response}
                        allowDeselect={true}
                        labLabels={
                          suggestion.category ===
                          DiseaseCategoryType.OutcomeTest
                        }
                      />
                    )
                  ) : (
                    <LevelSelect
                      onChange={onLevelSelectResponse}
                      qf={suggestion.finding}
                      response={response}
                    />
                  )}
                </ValueArea>
              )}
              {showRefine && (
                <RefineFeatureInline
                  causedByDisease={suggestion?.causedByDisease}
                  qf={getQfWithQualifiers()}
                  response={response}
                  relationId={relationId}
                  handleDone={onRefineEditDone}
                  patientCaseGrouping={PatientCaseGrouping.CLINICAL_FEATURES}
                />
              )}
            </>
          )}
        </Question>
        {props.showInlineReasoning && showClinicalReasoning && (
          <MobileClinicalReasoning
            suggestion={suggestion}
            show={!!props.showInlineReasoning && showClinicalReasoning}
          />
        )}
        {props.showActions && (
          <ShowMoreContainer>
            <ShowMore onClick={skipSuggestion}>Skip</ShowMore>
          </ShowMoreContainer>
        )}
      </NextStepPanelView>
    );
  },
);
